'use client';
import React from 'react';
import Accordion from '@/components/ui/Accordion';
import ContentWrapper from '@/components/common/ContentWrapper';
import ResponsiveSpacing from '@/components/common/ResponsiveSpacing';
import { faqItems } from '@/data/productData';
import { FAQItem } from '@/types/pdp';
import { useFAQ } from '@/hooks/useFAQ';
import { RichTextNode } from '@/types/faq';

interface FAQSectionProps {
  faqItems?: FAQItem[];
  useStrapiFAQ?: boolean;
  /** Solo SEO: título accesible "Preguntas frecuentes sobre {productName}" sin cambiar el diseño */
  productName?: string;
}

const FAQSection: React.FC<FAQSectionProps> = ({
  faqItems: customFaqItems,
  useStrapiFAQ = false,
  productName,
}) => {
  const { faqData, loading } = useFAQ();

  // Función para renderizar texto enriquecido de Strapi
  const renderRichText = (richTextNodes: RichTextNode[] | undefined): React.ReactNode => {
    if (!richTextNodes || richTextNodes.length === 0) return null;
    
    return richTextNodes.map((paragraph: any, pIndex: number) => {
      if (!paragraph.children) return null;
      
      return (
        <React.Fragment key={pIndex}>
          {paragraph.children.map((child: any, cIndex: number) => {
            if (child.bold) {
              return (
                <span data-component-id="src/components/sections/FAQSection.tsx:37:16" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="37" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-bold%22%7D" 
                  key={cIndex} 
                  className="font-tt-norms-bold"
                  style={{ fontFamily: "'TT_Norms_Pro-Bold', 'TT Norms Pro', sans-serif" }}
                >
                  {child.text}
                </span>
              );
            }
            return <React.Fragment key={cIndex}>{child.text}</React.Fragment>;
          })}
        </React.Fragment>
      );
    });
  };

  // Convertir preguntas de rich text a string simple para el título
  const richTextToString = (richTextNodes: RichTextNode[] | undefined): string => {
    if (!richTextNodes || richTextNodes.length === 0) return '';
    
    return richTextNodes
      .map(paragraph => 
        paragraph.children
          .map(child => child.text)
          .join('')
      )
      .join(' ');
  };

  // Convertir datos mock al formato de Strapi si no hay datos personalizados
  const mockFaqItems: FAQItem[] = faqItems.map((item, index) => ({
    id: index + 1,
    Pregunta: item.title,
    Respuesta: item.content
  }));

  // Determinar qué items mostrar
  let accordionItems;
  
  if (useStrapiFAQ && faqData?.data?.FAQ_Preguntas_Frecuentes) {
    // Usar datos de Strapi
    accordionItems = faqData.data.FAQ_Preguntas_Frecuentes.map((faq) => ({
      title: richTextToString(faq.Pregunta),
      content: renderRichText(faq.Respuesta)
    }));
  } else if (customFaqItems && customFaqItems.length > 0) {
    // Usar datos personalizados
    accordionItems = customFaqItems.map(item => ({
      title: item.Pregunta,
      content: item.Respuesta
    }));
  } else {
    // Usar datos mock
    accordionItems = mockFaqItems.map(item => ({
      title: item.Pregunta,
      content: item.Respuesta
    }));
  }

  // Estado de carga solo si estamos usando Strapi
  if (useStrapiFAQ && loading) {
    return (
      <ResponsiveSpacing data-component-id="src/components/sections/FAQSection.tsx:99:6" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="99" data-component-file="FAQSection.tsx" data-component-name="ResponsiveSpacing" data-component-content="%7B%22elementName%22%3A%22ResponsiveSpacing%22%7D" padding="lg">
        <div data-component-id="src/components/sections/FAQSection.tsx:100:8" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="100" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/components/sections/FAQSection.tsx:101:10" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="101" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-gray-300%20rounded%20mb-4%20w-96%22%7D" className="h-12 bg-gray-300 rounded mb-4 w-96"></div>
          <div data-component-id="src/components/sections/FAQSection.tsx:102:10" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="102" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gray-300%20rounded-none%20w-64%20h-8%20mb-12%22%7D" className="bg-gray-300 rounded-none w-64 h-8 mb-12"></div>
          <div data-component-id="src/components/sections/FAQSection.tsx:103:10" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="103" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            <div data-component-id="src/components/sections/FAQSection.tsx:104:12" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="104" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-gray-300%20rounded%22%7D" className="h-16 bg-gray-300 rounded"></div>
            <div data-component-id="src/components/sections/FAQSection.tsx:105:12" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="105" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-gray-300%20rounded%22%7D" className="h-16 bg-gray-300 rounded"></div>
            <div data-component-id="src/components/sections/FAQSection.tsx:106:12" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="106" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-gray-300%20rounded%22%7D" className="h-16 bg-gray-300 rounded"></div>
          </div>
        </div>
      </ResponsiveSpacing>
    );
  }
  
  return (
    <ResponsiveSpacing data-component-id="src/components/sections/FAQSection.tsx:114:4" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="114" data-component-file="FAQSection.tsx" data-component-name="ResponsiveSpacing" data-component-content="%7B%22elementName%22%3A%22ResponsiveSpacing%22%7D" padding="lg">
        {productName ? (
          <>
            <h2 data-component-id="src/components/sections/FAQSection.tsx:117:12" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="117" data-component-file="FAQSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22sr-only%22%2C%22textContent%22%3A%22Preguntas%20frecuentes%20sobre%22%7D" className="sr-only">Preguntas frecuentes sobre {productName}</h2>
            <p data-component-id="src/components/sections/FAQSection.tsx:118:12" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="118" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-global-1%20text-%5B40px%5D%20leading-%5B48px%5D%20mb-1%22%2C%22textContent%22%3A%22PREGUNTAS%20FRECUENTES%22%7D"
              className="text-global-1 text-[40px] leading-[48px] mb-1"
              style={{
                fontFamily: "'TT_Norms_Pro-Extra-Black', Helvetica",
                fontWeight: '900',
              }}
            >
              PREGUNTAS FRECUENTES
            </p>
          </>
        ) : (
          <h2 data-component-id="src/components/sections/FAQSection.tsx:129:10" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="129" data-component-file="FAQSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-global-1%20text-%5B40px%5D%20leading-%5B48px%5D%20mb-1%22%2C%22textContent%22%3A%22PREGUNTAS%20FRECUENTES%22%7D"
            className="text-global-1 text-[40px] leading-[48px] mb-1"
            style={{
              fontFamily: "'TT_Norms_Pro-Extra-Black', Helvetica",
              fontWeight: '900',
            }}
          >
            PREGUNTAS FRECUENTES
          </h2>
        )}
        <div data-component-id="src/components/sections/FAQSection.tsx:139:8" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="139" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded-none%20px-4%20w-fit%20mb-12%22%7D" className="bg-global-9 rounded-none px-4 w-fit mb-12">
          <span data-component-id="src/components/sections/FAQSection.tsx:140:10" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="140" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20text-%5B22px%5D%20leading-%5B27px%5D%22%2C%22textContent%22%3A%22Seguro%20que%20te%20interesa%22%7D" 
            className="text-global-1 text-[22px] leading-[27px]"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400'
            }}
          >
            Seguro que te interesa
          </span>
        </div>
        <Accordion data-component-id="src/components/sections/FAQSection.tsx:150:8" data-component-path="src/components/sections/FAQSection.tsx" data-component-line="150" data-component-file="FAQSection.tsx" data-component-name="Accordion" data-component-content="%7B%22elementName%22%3A%22Accordion%22%7D" items={accordionItems} />
    </ResponsiveSpacing>
  );
};

export default FAQSection; 